/**
 * CrmFieldValueInput — the single source of truth for rendering an editable
 * input for one CRM field (a property or an association, including pipeline
 * stages).
 *
 * Every surface that lets a user edit a field value renders through this
 * component so they stay identical: the slideout overview edit, the bulk-update
 * modal, the actions/proposals edit forms, and the inline "Update {field}"
 * record edit. It routes to the canonical leaf inputs:
 *   - property            → PropertyInput
 *   - pipeline-stage assoc → AssociatedPipelineStageInput (grouped by pipeline)
 *   - other association    → AssociatedCrmObjectInput
 *
 * This is label-free by design — callers own their own label/row chrome.
 *
 * Each `kind` lives in its own sub-component so hooks are never called
 * conditionally (the top-level switch renders one of them).
 */

import { useCallback, useMemo } from 'react';

import type { SelectSize } from '@/lib/base/forms/select/select-shared';
import type {
  DraftEntity,
  DraftParentInfo,
} from '@/lib/crm/core/draftEntities';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import type { CrmObject } from '@/types/crm';

import { AssociatedCrmObjectInput } from '../associations/AssociatedCrmObjectInput';
import {
  AssociatedPipelineStageInput,
  type StageAssociationValue,
} from '../properties/inputs/AssociatedPipelineStageInput';
import { PropertyInput } from '../properties/PropertyInput';

export interface PropertyFieldInputProps {
  kind: 'property';
  schema: PropertyTypeSchema;
  value: string | number | object | null | undefined;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  /** Organization ID, forwarded to PropertyInput for formula uuid-literal badges. */
  orgId?: number;
  /**
   * The entity's raw `properties` map (e.g. `object.properties`), forwarded
   * to PropertyInput so a read-only `formula` field can resolve its target
   * entity type from a sibling property.
   */
  entityProperties?: Record<string, unknown>;
  /**
   * Field size on the shared library scale, forwarded to PropertyInput.
   * Defaults to 'sm'. Hosts that need a compact field (a table cell's popped
   * field, a proposal card) pass 'xs' rather than reaching in with a
   * `[&_input]:text-sm` descendant override.
   */
  size?: SelectSize;
  /** Forwarded to PropertyInput; selects the text-leaf element. Defaults to 'form'. */
  textDisplay?: 'form' | 'card';
  /**
   * Forwarded to PropertyInput; how a markdown property's rich-text editor
   * renders (see PropertyInput.markdownDisplay). Defaults to 'form'.
   */
  markdownDisplay?: 'form' | 'card' | 'inline';
  /**
   * Forwarded to PropertyInput; viewport coordinates of the click that
   * opened the editor, so pixel-matched inline editors can drop the caret
   * at the clicked character. Currently consumed by markdown editors with
   * markdownDisplay='inline'; ignored otherwise.
   */
  initialCaretPoint?: { x: number; y: number } | null;
  /**
   * Open picker-style editors (select/boolean/date/datetime) immediately on
   * mount. Forwarded to PropertyInput. Used by inline click-to-edit surfaces
   * where the entry click already expressed "pick a value".
   */
  autoOpenPicker?: boolean;
}

export interface StageFieldInputProps {
  kind: 'stage';
  /** Source entity type (e.g. 'ahoy_deal') used to filter pipelines. */
  entityTypeName: string;
  /** Stage association type name for the onChange output. */
  associationTypeName: string;
  value: string | null | undefined;
  onChange: (value: StageAssociationValue | null) => void;
  disabled?: boolean;
  ariaLabel?: string;
  /** Suppress mount-time auto-select of the default pipeline's first stage. */
  disableAutoSelect?: boolean;
  /** Open the stage dropdown immediately on mount (inline click-to-edit). */
  defaultOpen?: boolean;
  /**
   * Trigger size on the shared field-control scale, forwarded to
   * AssociatedPipelineStageInput. Defaults to 'sm'; inline click-to-edit
   * rows pass 'xs' so the trigger has the same 32px footprint as a property
   * editor and the row keeps its height on entry.
   */
  size?: SelectSize;
}

export interface AssociationFieldInputProps {
  kind: 'association';
  associationType: AssociationType;
  /**
   * Restricts the picker to one target entity type. Set when the field renders
   * a single per-type subset of a kind-based rule, whose own
   * `target_entity_type_name` is empty because it resolves to a whole kind.
   */
  targetEntityTypeName?: string;
  value: string | string[] | null | undefined;
  onChange: (value: string | string[] | null) => void;
  orgId: number;
  /**
   * Stage entity-type names. When the association targets one, the grouped
   * pipeline-stage picker is rendered instead of the entity picker. Callers
   * get this from `useStageTypeNames`.
   */
  stageTypeNames?: Set<string>;
  disabled?: boolean;
  sourceEntityId?: string;
  onCustomCreate?: () => void;
  onBeforeCreate?: (proceed: () => void) => void;
  allowOverwrite?: boolean;
  hasSavedValue?: boolean;
  hideCreateOption?: boolean;
  /** Suppress the picker, leaving only the create affordance. See AssociatedCrmObjectInput. */
  createOnly?: boolean;
  onSelectedObjectsChange?: (objects: Record<string, CrmObject>) => void;
  /** Stage-only: suppress mount-time auto-select in edit-existing flows. */
  disableAutoSelect?: boolean;
  ariaLabel?: string;
  /**
   * Draft-mode create handler — when set, the leaf's "Create new" flow
   * builds a client-side DraftEntity and reports it here instead of eagerly
   * creating it on the server. Forwarded to AssociatedCrmObjectInput.
   */
  onCreateDraft?: (draft: DraftEntity) => void;
  /**
   * Parent-identity context threaded down from a parent draft form so a
   * nested create can render a "belongs to {parent}" banner. Forwarded to
   * AssociatedCrmObjectInput.
   */
  draftParentInfo?: DraftParentInfo;
  /**
   * Open the picker immediately on mount (inline click-to-edit surfaces).
   * Forwarded to AssociatedCrmObjectInput / AssociatedPipelineStageInput.
   */
  defaultOpen?: boolean;
  /**
   * Trigger size on the shared field-control scale, forwarded to
   * AssociatedCrmObjectInput / AssociatedPipelineStageInput. Defaults to
   * 'sm'; inline click-to-edit rows pass 'xs' (see StageFieldInputProps).
   */
  size?: SelectSize;
}

export type CrmFieldValueInputProps =
  | PropertyFieldInputProps
  | StageFieldInputProps
  | AssociationFieldInputProps;

function StageFieldInput({
  entityTypeName,
  associationTypeName,
  value,
  onChange,
  disabled,
  ariaLabel,
  disableAutoSelect,
  defaultOpen,
  size,
}: Omit<StageFieldInputProps, 'kind'>) {
  return (
    <AssociatedPipelineStageInput
      value={value ?? null}
      onChange={onChange}
      entityType={entityTypeName}
      associationTypeName={associationTypeName}
      disabled={disabled}
      ariaLabel={ariaLabel}
      disableAutoSelect={disableAutoSelect}
      defaultOpen={defaultOpen}
      size={size}
    />
  );
}

function AssociationFieldInput({
  associationType,
  targetEntityTypeName,
  value,
  onChange,
  orgId,
  stageTypeNames,
  disabled,
  sourceEntityId,
  onCustomCreate,
  onBeforeCreate,
  allowOverwrite,
  hasSavedValue,
  hideCreateOption,
  createOnly,
  onSelectedObjectsChange,
  disableAutoSelect,
  ariaLabel,
  onCreateDraft,
  draftParentInfo,
  defaultOpen,
  size,
}: Omit<AssociationFieldInputProps, 'kind'>) {
  const isStageAssociation =
    stageTypeNames?.has(associationType.target_entity_type_name) ?? false;

  // Pipeline-stage associations emit a single target_entity_id; normalize the
  // incoming value to a single id and forward the chosen stage's id back out
  // in the association's `string | null` shape.
  const stageValue = useMemo<string | null>(() => {
    if (typeof value === 'string') {
      return value;
    }
    if (Array.isArray(value)) {
      return value[0] ?? null;
    }
    return null;
  }, [value]);

  const handleStageChange = useCallback(
    (assoc: StageAssociationValue | null) => {
      onChange(assoc?.target_entity_id ?? null);
    },
    [onChange]
  );

  // Entity-picker associations are always array-shaped.
  const arrayValue = useMemo<string[] | null>(() => {
    if (value === null || value === undefined) {
      return null;
    }
    return Array.isArray(value) ? value : [value];
  }, [value]);

  if (isStageAssociation) {
    return (
      <AssociatedPipelineStageInput
        value={stageValue}
        onChange={handleStageChange}
        entityType={associationType.source_entity_type_name}
        associationTypeName={associationType.association_type_name}
        disabled={disabled}
        ariaLabel={ariaLabel ?? associationType.label}
        disableAutoSelect={disableAutoSelect}
        defaultOpen={defaultOpen}
        size={size}
      />
    );
  }

  return (
    <AssociatedCrmObjectInput
      associationType={associationType}
      targetEntityTypeName={targetEntityTypeName}
      value={arrayValue}
      onChange={onChange}
      orgId={orgId}
      disabled={disabled}
      sourceEntityId={sourceEntityId}
      onCustomCreate={onCustomCreate}
      onBeforeCreate={onBeforeCreate}
      allowOverwrite={allowOverwrite}
      hasSavedValue={hasSavedValue}
      hideCreateOption={hideCreateOption}
      createOnly={createOnly}
      onSelectedObjectsChange={onSelectedObjectsChange}
      onCreateDraft={onCreateDraft}
      draftParentInfo={draftParentInfo}
      defaultOpen={defaultOpen}
      size={size}
    />
  );
}

export function CrmFieldValueInput(props: CrmFieldValueInputProps) {
  if (props.kind === 'property') {
    return (
      <PropertyInput
        propertyType={props.schema}
        value={props.value}
        onChange={props.onChange}
        disabled={props.disabled}
        orgId={props.orgId}
        entityProperties={props.entityProperties}
        size={props.size}
        textDisplay={props.textDisplay}
        markdownDisplay={props.markdownDisplay}
        initialCaretPoint={props.initialCaretPoint}
        autoOpenPicker={props.autoOpenPicker}
      />
    );
  }
  if (props.kind === 'stage') {
    return <StageFieldInput {...props} />;
  }
  return <AssociationFieldInput {...props} />;
}
